<template>
    <div v-if="visible" class="copy-container">
        <div class="copy-content" ref="target">
            <ButtonSecondary
                size="s"
                :onClick="onCopy"
                iconPosition="left"
                icon="Wand"
                class="copy-content-button">
                {{ t({ key: 'copy.ferry.copy_to_all' }) }}
            </ButtonSecondary>

            <Toast v-if="message" icon="Check" color="green" padding="m">
                {{ message }}
            </Toast>
        </div>

        <Modal v-if="modal" :onClose="() => onSetModal(false)" size="s">
            <div class="copy-modal">
                <div class="copy-modal-content">
                    <p class="copy-modal-title">
                        {{ t({ key: 'copy.ferry.copy_details' }) }}
                    </p>
                    <p class="copy-modal-description">
                        {{ t({ key: 'copy.ferry.edit_later' }) }}
                    </p>
                    <p v-if="hasLicensePlates" class="copy-modal-description">
                        {{ t({ key: 'copy.ferry.note_license_plates' }) }}
                    </p>
                </div>

                <div class="copy-modal-buttons">
                    <ButtonPrimary :onClick="onApply">
                        {{ t({ key: 'copy.ferry.yes_copy' }) }}
                    </ButtonPrimary>
                    <ButtonText :onClick="() => onSetModal(false)">
                        {{ t({ key: 'cancellation.cancel' }) }}
                    </ButtonText>
                </div>
            </div>
        </Modal>
    </div>
</template>
<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType, ref, watch } from 'vue';
    import { Cart, FerryProduct, ProductType } from '@/api/Cart/cart.interfaces';
    import { FormValues } from '@ui/components/FormV2/Form.interfaces';
    import { copyFerries, showCopyFerry } from '../Copy.helpers';
    import { useStore } from '@/store';
    import { submitFormById } from '@/helpers/formRegistry';
    import { useElementVisibility } from '@vueuse/core';
    import ButtonSecondary from '@ui/components/Button/ButtonSecondary.vue';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';
    import ButtonText from '@ui/components/Button/ButtonText.vue';
    import Modal from '@ui/components/Modal/Modal.vue';
    import Toast from '@ui/components/Toast/Toast.vue';
    import {
        logStatsigCheckoutCopyFeatureApplied,
        logStatsigCheckoutCopyFeatureClick,
        logStatsigCheckoutCopyFeatureInvalidForm,
    } from '@/tracking/statsig/checkout.functions';

    export default defineComponent({
        name: 'CopyFerry',
        methods: { t },
        components: {
            ButtonPrimary,
            ButtonSecondary,
            ButtonText,
            Modal,
            Toast,
        },
        props: {
            product: {
                type: Object as PropType<FerryProduct>,
                required: true,
            },
            formId: { type: String, required: true },
            formValid: { type: Boolean, required: true },
            formValues: { type: Object as PropType<FormValues | null> },
        },
        setup(props) {
            const modal = ref<boolean>(false);
            const message = ref<string | null>(null);

            const store = useStore();

            const target = ref(null);
            const targetIsVisible = useElementVisibility(target);

            const cart = computed<Cart>(() => store.state.cart.data);

            const visible = computed<boolean>(() =>
                showCopyFerry(props.product.Id, cart.value)
            );

            const hasLicensePlates = computed<boolean>(() => {
                const plates = props.product.Legs.flatMap((l) => l.Segments).filter(
                    (s) => s.Vehicles
                );

                return plates.length > 0;
            });

            function onSetModal(state: boolean) {
                modal.value = state;
            }

            function onCopy() {
                logStatsigCheckoutCopyFeatureClick(ProductType.Ferry, cart.value);

                if (props.formValid) {
                    onSetModal(true);
                } else {
                    submitFormById(props.formId, true);
                    logStatsigCheckoutCopyFeatureInvalidForm(ProductType.Ferry, cart.value);
                }
            }

            function onApply() {
                const res = copyFerries(props.product.Id);

                if (res) {
                    message.value = t({
                        key: 'copy.ferry.copied_to_all_ferries',
                    });

                    onSetModal(false);

                    logStatsigCheckoutCopyFeatureApplied(ProductType.Ferry, cart.value);
                }
            }

            watch(targetIsVisible, (visible) => {
                if (message.value && !visible) {
                    message.value = null;
                }
            });

            watch(
                () => props.formValues,
                () => {
                    if (message.value) {
                        message.value = null;
                    }
                },
                { immediate: true, deep: true }
            );

            return {
                target,
                visible,
                message,
                modal,
                onSetModal,
                onApply,
                onCopy,
                hasLicensePlates,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward '../Copy.scss';
</style>
